import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";

/**
 * Public content queries.
 *
 * Published content changes rarely and is cached at the CDN in front of the API,
 * so the client keeps it fresh for five minutes. That removes a request from
 * every back-navigation without an editor's correction taking meaningfully
 * longer to appear.
 */

const CONTENT_STALE = 5 * 60 * 1000;

export function useBlogs(params) {
  return useQuery({
    queryKey: ["public", "blogs", params],
    queryFn: () => publicApi.blogs(params),
    staleTime: CONTENT_STALE,
    placeholderData: (previous) => previous,
  });
}

export function useBlog(slug) {
  return useQuery({
    queryKey: ["public", "blog", slug],
    queryFn: () => publicApi.blog(slug),
    staleTime: CONTENT_STALE,
    enabled: Boolean(slug),
    // A missing article is an answer, not a transient failure — retrying four
    // times just delays the 404 page.
    retry: (count, error) => error.status !== 404 && count < 2,
  });
}

export function useCaseStudies(params) {
  return useQuery({
    queryKey: ["public", "caseStudies", params],
    queryFn: () => publicApi.caseStudies(params),
    staleTime: CONTENT_STALE,
    placeholderData: (previous) => previous,
  });
}

export function useCaseStudy(slug) {
  return useQuery({
    queryKey: ["public", "caseStudy", slug],
    queryFn: () => publicApi.caseStudy(slug),
    staleTime: CONTENT_STALE,
    enabled: Boolean(slug),
    retry: (count, error) => error.status !== 404 && count < 2,
  });
}

export function useTestimonials(params) {
  return useQuery({
    queryKey: ["public", "testimonials", params],
    queryFn: () => publicApi.testimonials(params),
    staleTime: CONTENT_STALE,
  });
}

export function useAwards() {
  return useQuery({
    queryKey: ["public", "awards"],
    queryFn: publicApi.awards,
    staleTime: CONTENT_STALE,
  });
}

export function useSeo(pageKey) {
  return useQuery({
    queryKey: ["public", "seo", pageKey],
    queryFn: () => publicApi.seo(pageKey),
    staleTime: CONTENT_STALE,
    enabled: Boolean(pageKey),
  });
}
